import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, model, signal } from '@angular/core';

const KEY_STEP = 0.02;

/**
 * Two panes with a draggable divider between them, top/bottom
 * (`orientation="vertical"`) or left/right (`"horizontal"`). `ratio` is the
 * first pane's share (0–1) and is two-way bound, so the caller can persist
 * it. The divider is a real separator: focusable, arrow keys move it,
 * Home/End jump to the limits, double-click resets to `defaultRatio`.
 *
 *   <app-split-pane [(ratio)]="codeRatio" orientation="vertical" [minStart]="120" [minEnd]="200">
 *     <section splitStart>…</section>
 *     <section splitEnd>…</section>
 *   </app-split-pane>
 */
@Component({
  selector: 'app-split-pane',
  standalone: false,
  template: `
    <div class="split__pane" [style.flex-basis.%]="ratio() * 100"><ng-content select="[splitStart]" /></div>
    <div
      class="split__handle"
      role="separator"
      tabindex="0"
      [attr.aria-orientation]="orientation() === 'vertical' ? 'horizontal' : 'vertical'"
      [attr.aria-valuenow]="percent()"
      aria-valuemin="0"
      aria-valuemax="100"
      [attr.aria-label]="label()"
      [class.split__handle--dragging]="dragging()"
      (pointerdown)="startDrag($event)"
      (keydown)="onKeydown($event)"
      (dblclick)="ratio.set(defaultRatio())"
    >
      <span class="split__grip" aria-hidden="true"></span>
    </div>
    <div class="split__pane split__pane--end"><ng-content select="[splitEnd]" /></div>
  `,
  styleUrl: './split-pane.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'split',
    '[class.split--vertical]': 'orientation() === "vertical"',
    '[class.split--horizontal]': 'orientation() === "horizontal"',
    '[class.split--dragging]': 'dragging()',
  },
})
export class SplitPane {
  readonly orientation = input<'vertical' | 'horizontal'>('vertical');
  readonly ratio = model(0.4);
  readonly defaultRatio = input(0.4);
  /** Smallest size, in px, either pane can be dragged to. */
  readonly minStart = input(120);
  readonly minEnd = input(160);
  readonly label = input('Resize panels');

  protected readonly dragging = signal(false);
  protected readonly percent = computed(() => Math.round(this.ratio() * 100));

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected startDrag(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    handle.focus();
    this.dragging.set(true);

    const move = (e: PointerEvent) => this.setFromPointer(e);
    const end = () => {
      this.dragging.set(false);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const vertical = this.orientation() === 'vertical';
    const back = vertical ? 'ArrowUp' : 'ArrowLeft';
    const fwd = vertical ? 'ArrowDown' : 'ArrowRight';
    let next: number | null = null;
    if (event.key === back) next = this.ratio() - KEY_STEP;
    else if (event.key === fwd) next = this.ratio() + KEY_STEP;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = 1;
    if (next === null) return;
    event.preventDefault();
    this.ratio.set(this.clamp(next));
  }

  private setFromPointer(event: PointerEvent): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const vertical = this.orientation() === 'vertical';
    const size = vertical ? rect.height : rect.width;
    if (!size) return;
    const offset = vertical ? event.clientY - rect.top : event.clientX - rect.left;
    this.ratio.set(this.clamp(offset / size));
  }

  /** Keeps both panes at least their minimum size. */
  private clamp(ratio: number): number {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const size = this.orientation() === 'vertical' ? rect.height : rect.width;
    if (!size) return Math.min(0.9, Math.max(0.1, ratio));
    const min = this.minStart() / size;
    const max = 1 - this.minEnd() / size;
    return Math.min(Math.max(ratio, min), Math.max(min, max));
  }
}
